import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const useAuth = (onUserProgressLoad) => {
  // Auth state
  const [user, setUser] = useState(null);
  const [guestMode, setGuestMode] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'signup'
  const [authError, setAuthError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Load user progress from database (now supports per-deck tracking)
  const loadUserProgress = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('character_index, known, times_seen, deck_id')
        .eq('user_id', userId);

      if (error) throw error;

      if (data && data.length > 0 && onUserProgressLoad) {
        // Group progress by deck_id
        const progressByDeck = {};

        data.forEach(p => {
          const deckId = p.deck_id || 'characters'; // Default to characters for old data
          if (!progressByDeck[deckId]) {
            progressByDeck[deckId] = { known: new Set(), learning: new Set() };
          }

          if (p.known === true) {
            progressByDeck[deckId].known.add(p.character_index);
          } else {
            progressByDeck[deckId].learning.add(p.character_index);
          }
        });

        onUserProgressLoad(progressByDeck);
      }
    } catch (err) {
      console.error('Error loading progress:', err);
    }
  }, [onUserProgressLoad]);

  // Check auth state on mount
  useEffect(() => {
    // Get initial session and load progress if user exists
    supabase.auth.getSession().then(({ data }) => {
      const currentUser = data.session?.user ?? null;
      setUser(currentUser);
      setAuthLoading(false);

      // Load progress for existing session (important for app reload/refresh)
      if (currentUser) {
        loadUserProgress(currentUser.id);
      }
    });

    // Also listen for auth state changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProgress(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadUserProgress]);

  // Auth functions
  const handleSignUp = async (e) => {
    e.preventDefault();
    setAuthError(null);
    try {
      // Determine the redirect URL based on environment
      // For web: use the current origin
      // For native: this won't be used (handled by deep links)
      const redirectUrl = window.location.origin;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) {
        // "Database error saving new user" means auth succeeded but a DB trigger failed.
        // The user was still created and will receive the confirmation email,
        // so treat it as a success on the client side.
        if (error.message?.includes('Database error')) {
          console.warn('Supabase DB trigger error (user was still created):', error.message);
          setAuthError('Check your email for the confirmation link!');
          return;
        }
        throw error;
      }

      setAuthError('Check your email for the confirmation link!');
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleSignInWithGoogle = async () => {
    setAuthError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({ provider: 'google' });
      if (error) throw error;
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setGuestMode(false);
  };

  const continueAsGuest = () => {
    setGuestMode(true);
  };

  return {
    // State
    user,
    guestMode,
    setGuestMode,
    authLoading,
    authMode,
    setAuthMode,
    authError,
    email,
    setEmail,
    password,
    setPassword,
    // Functions
    handleSignUp,
    handleSignIn,
    handleSignInWithGoogle,
    handleSignOut,
    continueAsGuest,
  };
};
